<template>
  <div class="form">
    <router-link :to="translations.i18nRoute({ name: 'profile' })">
      <svg width="25" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.0413 20L4.04126 12L12.0413 4L13.0913 5.05L6.89126 11.25H20.0413V12.75H6.89126L13.0913 18.95L12.0413 20Z"
            fill="currentColor"/>
    </svg>
    </router-link>
    <p class="form__title">{{ t('buttons.edit') }}</p>
    <div class="form__inputs">
      <ui-input
        :type="'text'"
        :placeholder="t('fields.fname')"
        v-model="firstName"
      />
      <ui-input
        :type="'text'"
        :placeholder="t('fields.lname')"
        v-model="lastName"
      />
      <ui-input
        :type="'email'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
      />
      <ui-input
        :type="'password'"
        :placeholder="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
      />
      <ui-input
        :type="'password'"
        :placeholder="t('fields.passwordConfirm')"
        :rules="[compareStrings]"
        v-model="passwordConfirm"
      />
    </div>
    <button
        class="form__button"
        @click="update"
    >
      {{ t('buttons.edit') }}
    </button>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isEmail, isPasswordValid} from "@/core/rules/textFieldRules.js";
import {computed, ref} from "vue";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {Auth} from "@/core/api/auth.js";
import {useRouter} from "vue-router";
import translations from "@/core/helpers/translations.js";

const {t} = useI18n()
const authStore = useAuthStore()
const router = useRouter()

const userUuid = computed(() => {
  return authStore.user ? authStore.user.uuid : ''
})
const accessToken = computed(() => authStore.accessToken)
const userEmail = computed(() => {
  return authStore.user.email ? authStore.user.email : ''
})
const userFirstName = computed(() => {
  return authStore.user.firstName ? authStore.user.firstName : ''
})
const userLastName = computed(() => {
  return authStore.user.lastName ? authStore.user.lastName : ''
})

const firstName = ref(userFirstName.value)
const lastName = ref(userLastName.value)
const email = ref(userEmail.value)
const password = ref('')
const passwordConfirm = ref('')

const isLoading = ref(false)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('gce-refresh')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('gce-user-locale') || 'en-gb'
    }
  })
}

const update = async () => {
  isLoading.value = true

  const res = await Auth.updateUser(
    `uuid: "${userUuid.value}"` +
      (email.value ? `, email: "${email.value}"` : "") +
      (firstName.value ? `, firstName: "${firstName.value}"` : "") +
      (lastName.value ? `, lastName: "${lastName.value}"` : "") +
      (password.value ? `, password: "${password.value}"` : "") +
      (passwordConfirm.value ? `, confirmPassword: "${passwordConfirm.value}"` : ""),
      accessToken.value
  )

  if (!res.errors) {
    ElNotification({
      message: t('popup.successUpdate'),
      type: 'success'
    })

    if (userEmail.value !== email.value) {
      await logout()
    } else {
      ElNotification({
        message: t('popup.successUpdate'),
        type: 'success'
      })

      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })

      // if (res.data.updateUser.user.language !== translation.currentLocale) {
      //   translation.switchLanguage(res.data.updateUser.user.language)
      // }
    }
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }
  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 450px;
  margin-block: 85px;
  border-radius: 40px;
  background-color: $white;
  padding: 45px 35px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;
  position: relative;

  & a {
    position: absolute;
    left: 20px;
    top: 20px;
  }

  & svg {
    cursor: pointer;
    color: $black;
    transition: 0.2s;

    @include hover {
      color: #595959;
    }
  }

  &__title {
    width: 100%;
    text-align: center;
    border-bottom: 1px solid $black;
    padding-bottom: 15px;
    color: $black;
    font-family: Poppins, sans-serif;
    font-size: 20px;
    font-weight: 600;
  }

  &__inputs {
    width: 100%;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  &__button {
    cursor: pointer;
    width: 100%;
    border-radius: 8px;
    background-color: rgba(47, 107, 79, 0.4);
    padding-block: 3px;
    display: grid;
    place-items: center;
    transition: 0.2s;

    color: $white;
    font-family: Poppins, sans-serif;
    font-size: 20px;
    font-weight: 600;

    @include hover {
      background-color: rgba(47, 107, 79, 0.7);
    }
  }
}

@media (max-width: 1300px) {
  .form {
    margin-block: 40px;
  }
}

@media (max-width: 500px) {
  .form {
    width: 100%;
  }
}
</style>